import {
    normalizeThemeColorValue,
    isValidCssLengthValue,
    isValidFontFamilyValue,
} from '@ai-content-hub/shared-styles';
import type { ThemeConfig } from '../schema';

export interface ContentThemeLayers {
    /** Registered per content type (no registration API yet — layer reserved). */
    preset?: ThemeConfig;
    /** Persisted item config.theme. */
    config?: ThemeConfig;
    /** Explicit host override (e.g. ContentWrapperFull `theme` prop). */
    override?: ThemeConfig;
    /** When true, resolution yields undefined and host tokens win. */
    useHostTheme?: boolean;
}

const THEME_COLOR_KEYS = [
    'primaryColor',
    'secondaryColor',
    'successColor',
    'errorColor',
    'warningColor',
    'backgroundColor',
    'textColor',
] as const satisfies readonly (keyof ThemeConfig)[];

/** Fields validated as a CSS length (e.g. `8px`, `1rem`) rather than a color. */
const THEME_LENGTH_KEYS = ['borderRadius', 'fontSize'] as const satisfies readonly (keyof ThemeConfig)[];

/** Drop keys whose value is `undefined` so an explicit `{ key: undefined }` in a later layer can't clobber an earlier layer's set value when spread. */
function pickDefined(theme: ThemeConfig | undefined): ThemeConfig {
    if (!theme) return {};
    const picked: ThemeConfig = {};
    for (const [key, value] of Object.entries(theme) as [keyof ThemeConfig, string | undefined][]) {
        if (value !== undefined) picked[key] = value;
    }
    return picked;
}

// EAA-1671: `config.theme` is persisted, admin/author-editable JSON — sanitize
// it through the same choke-point patterns shared-styles' tenant-theme layer
// uses (colors via normalizeThemeColorValue, CSS lengths via
// isValidCssLengthValue, font-family via isValidFontFamilyValue) before any
// field reaches a CSS custom property. Invalid values are dropped rather than
// failing the whole merge, matching `sanitizeThemeConfig`'s partial-validity
// behavior for the tenant layer. preset/override are package/host code, not
// persisted data, so they are not run through this — only `config` is.
function sanitizeThemeConfigLayer(theme: ThemeConfig | undefined): ThemeConfig | undefined {
    if (!theme) return undefined;
    const sanitized: ThemeConfig = { ...theme };

    for (const key of THEME_COLOR_KEYS) {
        const value = theme[key];
        if (value === undefined) continue;
        const normalized = normalizeThemeColorValue(value);
        if (normalized === null) delete sanitized[key];
        else sanitized[key] = normalized;
    }

    for (const key of THEME_LENGTH_KEYS) {
        const value = theme[key];
        if (value !== undefined && !isValidCssLengthValue(value)) delete sanitized[key];
    }

    if (theme.fontFamily !== undefined && !isValidFontFamilyValue(theme.fontFamily)) {
        delete sanitized.fontFamily;
    }

    return sanitized;
}

/**
 * Sparse-merge the content theme layers, most specific wins:
 * override > config > preset. `useHostTheme` short-circuits to `undefined`
 * so host tokens (e.g. the tenant `:root` cascade) win outright — same
 * semantics as `ContentPlayer.tsx`'s `useHostTheme ? undefined : config.theme`.
 */
export function resolveContentTheme(layers: ContentThemeLayers): ThemeConfig | undefined {
    if (layers.useHostTheme) return undefined;

    const merged: ThemeConfig = {
        ...pickDefined(layers.preset),
        ...pickDefined(sanitizeThemeConfigLayer(layers.config)),
        ...pickDefined(layers.override),
    };

    return Object.keys(merged).length > 0 ? merged : undefined;
}
